import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { BehaviorSubject, Subject, firstValueFrom, fromEvent } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";
import { protectedResources } from "../auth/auth-config";
import { environment } from "src/environments/environment";

/** How long before the B2C refresh token expires the session timeout banner appears. */
export const SESSION_WARN_BEFORE_MS = 60 * 60 * 1000;
/**
 * How long before the access token expires we force a renewal (and show the banner if it fails).
 * Must be more than MSAL's own 5 minute renewal offset, otherwise an API call made by the
 * MsalInterceptor in that window can redirect to login before this check runs.
 * Short-lived tokens are checked halfway through their remaining lifetime instead (see scheduleRenewalCheck).
 */
export const RENEWAL_CHECK_BEFORE_MS = 10 * 60 * 1000;

// B2C refresh tokens for SPAs last 24 hours from sign-in and renewals don't extend them. When one
// expires, MSAL renews silently through a hidden iframe if the B2C sign-in cookie is still valid (the
// new refresh token then hides the banner); only when that fails too does the MsalInterceptor redirect to login.
const B2C_SPA_SESSION_MS = 24 * 60 * 60 * 1000;
// setTimeout overflows above this value (~24.8 days) and fires immediately
const MAX_TIMEOUT_MS = 2_147_483_647;
// Retry delay when renewal fails only because the browser is offline
const OFFLINE_RETRY_MS = 30 * 1000;
// The refresh token expiry can shift by a few seconds between renewals; treat it as the same warning
const SAME_WARNING_TOLERANCE_MS = 2 * 60 * 1000;
// MSAL renews an access token this long before it expires (default tokenRenewalOffsetSeconds), on the
// next API call. Once the session can't be renewed, that renewal fails and the interceptor redirects.
const MSAL_RENEWAL_OFFSET_MS = 5 * 60 * 1000;

// Logs expiry times only (never tokens) with console.debug. Keep false in committed code.
const DEBUG_TOKEN_EXPIRY = false;

// ---- TEMPORARY DEV-ONLY TESTING CONFIG — REMOVE BEFORE MERGING ----
// Ignored in production builds. With ENABLED: true the refresh token is treated as ending 3 minutes
// after the page loads, so the banner appears after 1 minute showing "2 minutes" and counts down.
// Access tokens are treated as lasting 2 minutes and are renewed 1 minute before expiry.
// Add SIMULATE_RENEWAL_FAILURE: true to fake a failed renewal and see the final warning.
const DEV_EXPIRY_SIMULATION = {
  ENABLED: false,
  SIMULATE_RENEWAL_FAILURE: false,
  SESSION_ENDS_AFTER_MS: 3 * 60 * 1000,
  SESSION_WARN_BEFORE_MS: 2 * 60 * 1000,
  TOKEN_LIFETIME_MS: 2 * 60 * 1000,
  RENEWAL_CHECK_BEFORE_MS: 60 * 1000,
  MSAL_RENEWAL_OFFSET_MS: 30 * 1000,
};
// ---- END TEMPORARY DEV-ONLY TESTING CONFIG ----

interface IMsalCachedRefreshToken {
  credentialType?: string;
  clientId?: string;
  homeAccountId?: string;
  expiresOn?: string;
}

@Injectable({
  providedIn: "root",
})
export class TokenExpiryWarningService implements OnDestroy {
  /** When the user will be redirected to login while the session timeout banner shows; null hides it. */
  sessionTimeoutWarning$ = new BehaviorSubject<Date | null>(null);

  /** Overridable for tests. */
  sessionWarnBeforeMs = this.isDevSimulation()
    ? DEV_EXPIRY_SIMULATION.SESSION_WARN_BEFORE_MS
    : SESSION_WARN_BEFORE_MS;
  renewalCheckBeforeMs = this.isDevSimulation()
    ? DEV_EXPIRY_SIMULATION.RENEWAL_CHECK_BEFORE_MS
    : RENEWAL_CHECK_BEFORE_MS;
  private readonly msalRenewalOffsetMs = this.isDevSimulation()
    ? DEV_EXPIRY_SIMULATION.MSAL_RENEWAL_OFFSET_MS
    : MSAL_RENEWAL_OFFSET_MS;

  private readonly scopes = protectedResources.apiTodoList.scopes.read;
  private readonly devSessionEndsOn = new Date(Date.now() + DEV_EXPIRY_SIMULATION.SESSION_ENDS_AFTER_MS);
  private accessTokenExpiresOn: Date | null = null;
  private sessionEndsOn: Date | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionWarningTimer: ReturnType<typeof setTimeout> | null = null;
  private renewalInProgress: Promise<void> | null = null;
  // "session": the refresh token has less than an hour left. "final": the last access token renewal failed.
  private warningKind: "session" | "final" | null = null;
  // What the user closed, so the banner doesn't come back: the session warning stays closed for that
  // refresh token even when its redirect time moves; the final warning still shows once as a last reminder
  private dismissedSessionEndsAt: number | null = null;
  private dismissedFinalAt: number | null = null;
  private started = false;
  private _destroying$ = new Subject<void>();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private ngZone: NgZone
  ) {}

  /**
   * Starts following the current tokens. Call once MSAL has finished handling
   * the redirect and an account is active. Safe to call multiple times.
   */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    // Every new token (login, interceptor renewal, our own renewal) reschedules both checks
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((msg: EventMessage) =>
        this.handleNewToken(msg.payload as AuthenticationResult)
      );

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => this.clear());

    // Background tabs throttle timers, so re-check the real remaining time when the tab is shown
    fromEvent(document, "visibilitychange")
      .pipe(
        filter(() => document.visibilityState === "visible"),
        takeUntil(this._destroying$)
      )
      .subscribe(() => this.recheck());

    // The cache is in localStorage: another tab renewing the token or logging in again writes
    // new MSAL entries here. Re-read the current token so this tab drops stale timers/banners.
    fromEvent<StorageEvent>(window, "storage")
      .pipe(
        filter((event) => this.isMsalTokenStorageKey(event.key)),
        debounceTime(500),
        takeUntil(this._destroying$)
      )
      .subscribe(() => this.syncFromCache());

    // Timers don't survive a reload: schedule from the cached token once MSAL is idle
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (!this.accessTokenExpiresOn) {
          this.syncFromCache();
        }
      });
  }

  /** Stops everything; start() can be called again afterwards. */
  stop(): void {
    this._destroying$.next();
    this._destroying$.complete();
    this._destroying$ = new Subject<void>();
    this.started = false;
    this.clear();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Called when the user closes the banner. */
  dismissWarning(): void {
    const current = this.sessionTimeoutWarning$.value;
    if (!current) {
      return;
    }
    if (this.warningKind === "final") {
      this.dismissedFinalAt = current.getTime();
    } else {
      this.dismissedSessionEndsAt = this.sessionEndsOn?.getTime() ?? null;
    }
    this.sessionTimeoutWarning$.next(null);
  }

  private handleNewToken(result: AuthenticationResult | null): void {
    if (!result?.accessToken || !result.expiresOn || !this.isForApiScopes(result)) {
      return;
    }
    this.accessTokenExpiresOn = this.isDevSimulation()
      ? new Date(Date.now() + DEV_EXPIRY_SIMULATION.TOKEN_LIFETIME_MS)
      : new Date(result.expiresOn);
    this.sessionEndsOn = this.getSessionEndsOn(result.account);
    this.debugLog(
      `access token expires ${this.accessTokenExpiresOn.toISOString()}, ` +
      `refresh token expires ${this.sessionEndsOn?.toISOString() ?? "unknown"}`
    );

    // Logged in again (or another tab did): the session end moved out of the warning window
    const stillInWarningWindow = this.sessionEndsOn &&
      this.sessionEndsOn.getTime() - Date.now() <= this.sessionWarnBeforeMs;
    if (this.sessionTimeoutWarning$.value && !stillInWarningWindow) {
      this.sessionTimeoutWarning$.next(null);
    }
    this.scheduleRenewalCheck();
    this.scheduleSessionWarning();
  }

  private scheduleRenewalCheck(): void {
    this.clearTimer("renewal");
    if (!this.accessTokenExpiresOn) {
      return;
    }
    const expiresAt = this.accessTokenExpiresOn.getTime();
    const now = Date.now();
    // B2C access tokens can be as short as 5 minutes (e.g. expires_in 600): never check sooner than
    // halfway through the remaining lifetime, or each renewed token would trigger the next renewal at once.
    // Already expired tokens still check immediately.
    const checkAt = Math.max(expiresAt - this.renewalCheckBeforeMs, now + (expiresAt - now) / 2);
    this.renewalTimer = this.setZoneTimer(
      checkAt,
      () => {
        this.renewalTimer = null;
        this.renew();
      }
    );
  }

  private scheduleSessionWarning(): void {
    this.clearTimer("session");
    if (!this.sessionEndsOn) {
      return;
    }
    const sessionEndsOn = this.sessionEndsOn;
    this.sessionWarningTimer = this.setZoneTimer(
      sessionEndsOn.getTime() - this.sessionWarnBeforeMs,
      () => {
        this.sessionWarningTimer = null;
        this.showWarning(this.predictRedirectAt(), "session");
      }
    );
  }

  /**
   * Runs the callback inside NgZone at the given time. Past times fire at once; times beyond the
   * setTimeout maximum wait the maximum and then re-check.
   */
  private setZoneTimer(fireAt: number, callback: () => void): ReturnType<typeof setTimeout> {
    const delay = Math.max(fireAt - Date.now(), 0);
    let handle: ReturnType<typeof setTimeout>;
    this.ngZone.runOutsideAngular(() => {
      handle = setTimeout(() => {
        this.ngZone.run(() => {
          if (fireAt - Date.now() > 0) {
            this.setZoneTimer(fireAt, callback);
            return;
          }
          callback();
        });
      }, Math.min(delay, MAX_TIMEOUT_MS));
    });
    return handle;
  }

  private recheck(): void {
    // Recomputes delays from now, firing at once if a deadline passed while the tab was hidden
    if (this.accessTokenExpiresOn && !this.renewalInProgress) {
      this.scheduleRenewalCheck();
    }
    if (this.sessionEndsOn && !this.sessionTimeoutWarning$.value) {
      this.scheduleSessionWarning();
    }
  }

  /**
   * Tries to renew. Success needs no handling: MSAL emits ACQUIRE_TOKEN_SUCCESS and
   * handleNewToken() reschedules for the new token.
   */
  private renew(): Promise<void> {
    // One renewal at a time; MSAL also de-duplicates identical silent requests from the interceptor
    if (this.renewalInProgress) {
      return this.renewalInProgress;
    }
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      return Promise.resolve();
    }

    this.renewalInProgress = this.waitForNoInteraction()
      .then(() => {
        if (this.isDevSimulation() && DEV_EXPIRY_SIMULATION.SIMULATE_RENEWAL_FAILURE) {
          throw new InteractionRequiredAuthError("dev_simulated_failure");
        }
        return this.authService.instance.acquireTokenSilent({
          scopes: this.scopes,
          account,
          forceRefresh: true,
        });
      })
      .then(() => undefined)
      .catch((error) => this.handleRenewalFailure(error))
      .finally(() => (this.renewalInProgress = null));
    return this.renewalInProgress;
  }

  private handleRenewalFailure(error: unknown): void {
    // Offline is temporary; the interceptor will retry once the network is back, so do we
    if (!(error instanceof InteractionRequiredAuthError) && !navigator.onLine) {
      this.debugLog("renewal failed while offline, retrying later");
      this.clearTimer("renewal");
      this.renewalTimer = this.setZoneTimer(Date.now() + OFFLINE_RETRY_MS, () => {
        this.renewalTimer = null;
        this.renew();
      });
      return;
    }
    // InteractionRequiredAuthError (refresh token expired and the B2C cookie couldn't renew it) or any
    // other error: the MsalInterceptor redirects to login on any silent failure once the access token runs out
    this.debugLog(`renewal failed (${(error as Error)?.name}), showing session timeout banner`);
    const redirectAt = this.accessTokenExpiresOn
      ? new Date(this.accessTokenExpiresOn.getTime() - this.msalRenewalOffsetMs)
      : new Date();
    this.showWarning(redirectAt, "final");
  }

  /** Reads the current API token from the cache (no forced refresh) and schedules from it. */
  private syncFromCache(): void {
    const account = this.authService.instance.getActiveAccount();
    if (!account) {
      return;
    }
    this.authService.instance
      .acquireTokenSilent({ scopes: this.scopes, account })
      .then((result) => this.handleNewToken(result))
      .catch(() => {
        // No silent way to get a token: the interceptor redirects to login on the next API call
      });
  }

  private showWarning(redirectAt: Date, kind: "session" | "final"): void {
    const dismissed = kind === "session"
      ? this.isSameWarning(this.dismissedSessionEndsAt, this.sessionEndsOn)
      : this.isSameWarning(this.dismissedFinalAt, redirectAt);
    if (dismissed) {
      return;
    }
    this.warningKind = kind;
    if (this.sessionTimeoutWarning$.value?.getTime() !== redirectAt.getTime()) {
      this.sessionTimeoutWarning$.next(redirectAt);
    }
  }

  /**
   * When the redirect to login happens once the session can't be renewed: the first access token
   * renewal after the refresh token expires. Renewals before that still work through the refresh token,
   * so it's the later of the refresh token expiry and the current access token's renewal time.
   * Before the last renewal this can be earlier than the real redirect, never later.
   */
  private predictRedirectAt(): Date {
    const times = [
      this.sessionEndsOn?.getTime(),
      this.accessTokenExpiresOn ? this.accessTokenExpiresOn.getTime() - this.msalRenewalOffsetMs : undefined,
    ].filter((time): time is number => time != null);
    return new Date(times.length ? Math.max(...times) : Date.now());
  }

  private isSameWarning(time: number | null | undefined, other: Date | null): boolean {
    return time != null && other != null && Math.abs(time - other.getTime()) < SAME_WARNING_TOLERANCE_MS;
  }

  /**
   * When the login session ends: the refresh token's expiresOn from the MSAL cache
   * (B2C sends refresh_token_expires_in), else sign-in time + 24h, else unknown.
   */
  private getSessionEndsOn(account: AccountInfo | null): Date | null {
    if (this.isDevSimulation()) {
      return this.devSessionEndsOn;
    }
    account = account ?? this.authService.instance.getActiveAccount();
    if (!account) {
      return null;
    }
    const refreshTokenExpiresOn = this.readRefreshTokenExpiresOn(account.homeAccountId);
    if (refreshTokenExpiresOn) {
      return new Date(refreshTokenExpiresOn * 1000);
    }
    const authTime = Number(account.idTokenClaims?.["auth_time"]);
    return authTime ? new Date(authTime * 1000 + B2C_SPA_SESSION_MS) : null;
  }

  /** Returns the cached refresh token's expiresOn (epoch seconds). Only that field is used, never the token. */
  private readRefreshTokenExpiresOn(homeAccountId: string): number | null {
    let latest: number | null = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.toLowerCase().includes("refreshtoken")) {
        continue;
      }
      const entry = this.parseCacheEntry(localStorage.getItem(key));
      if (
        entry?.credentialType !== "RefreshToken" ||
        entry.clientId !== environment.b2c.clientId ||
        entry.homeAccountId !== homeAccountId
      ) {
        continue;
      }
      const expiresOn = Number(entry.expiresOn);
      if (expiresOn && (latest === null || expiresOn > latest)) {
        latest = expiresOn;
      }
    }
    return latest;
  }

  private parseCacheEntry(value: string | null): IMsalCachedRefreshToken | null {
    try {
      return value ? (JSON.parse(value) as IMsalCachedRefreshToken) : null;
    } catch {
      return null;
    }
  }

  private clear(): void {
    this.clearTimer("renewal");
    this.clearTimer("session");
    this.accessTokenExpiresOn = null;
    this.sessionEndsOn = null;
    this.warningKind = null;
    this.dismissedSessionEndsAt = null;
    this.dismissedFinalAt = null;
    this.sessionTimeoutWarning$.next(null);
  }

  private clearTimer(timer: "renewal" | "session"): void {
    if (timer === "renewal" && this.renewalTimer) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    if (timer === "session" && this.sessionWarningTimer) {
      clearTimeout(this.sessionWarningTimer);
      this.sessionWarningTimer = null;
    }
  }

  private waitForNoInteraction(): Promise<InteractionStatus> {
    return firstValueFrom(
      this.msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
    );
  }

  /** Only follow tokens for the API the interceptor protects, not e.g. ID-token-only logins. */
  private isForApiScopes(result: AuthenticationResult): boolean {
    if (!result.scopes?.length) {
      return true;
    }
    const granted = result.scopes.map((s) => s.toLowerCase());
    return this.scopes.some((scope) => granted.includes(scope.toLowerCase()));
  }

  private isMsalTokenStorageKey(key: string | null): boolean {
    // key is null when another tab calls localStorage.clear(); logout is handled by ACCOUNT_REMOVED
    if (key === null) {
      return false;
    }
    const lowerKey = key.toLowerCase();
    return lowerKey.includes(environment.b2c.clientId.toLowerCase()) &&
      (lowerKey.includes("accesstoken") || lowerKey.includes("refreshtoken") ||
        lowerKey.startsWith("msal.token.keys"));
  }

  private isDevSimulation(): boolean {
    return !environment.production && DEV_EXPIRY_SIMULATION.ENABLED;
  }

  private debugLog(message: string): void {
    if (DEBUG_TOKEN_EXPIRY && !environment.production) {
      console.debug(`[TokenExpiryWarning] ${message}`);
    }
  }
}
